import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../permissions";
import { IconLoader, IconAlert, IconChevronLeft, IconX } from "../components/Icons";

type TableShape = "round" | "square" | "rectangle";

interface FloorMapTable {
  id: string;
  number: string;
  seats: number;
  x: number;
  y: number;
  width: number;
  height: number;
  shape: TableShape;
  rotation: number;
}

interface FloorMapArea {
  id: string;
  name: string;
  width: number;
  height: number;
  tables: FloorMapTable[];
}

const CELL_PX = 28;
const EMPTY_UUID = "00000000-0000-0000-0000-000000000000";

function shapeSize(shape: TableShape, rotated: boolean): { w: number; h: number } {
  switch (shape) {
    case "round":
    case "square":
      return { w: 2, h: 2 };
    case "rectangle":
      return rotated ? { w: 2, h: 3 } : { w: 3, h: 2 };
  }
}

function newTable(
  x: number,
  y: number,
  shape: TableShape,
  rotated: boolean,
  number: string,
): FloorMapTable {
  const { w, h } = shapeSize(shape, rotated);
  return {
    id: crypto.randomUUID(),
    number,
    seats: 4,
    x,
    y,
    width: w,
    height: h,
    shape,
    rotation: rotated ? 90 : 0,
  };
}

function tableAt(tables: FloorMapTable[], x: number, y: number): FloorMapTable | undefined {
  return tables.find(
    (t) => x >= t.x && x < t.x + t.width && y >= t.y && y < t.y + t.height,
  );
}

function nextNumber(tables: FloorMapTable[]): string {
  const nums = tables
    .map((t) => parseInt(t.number, 10))
    .filter((n) => Number.isFinite(n));
  const max = nums.length ? Math.max(...nums) : 0;
  return String(max + 1);
}

function ShapeIcon({
  shape,
  rotated,
  size,
  color,
  fill,
}: {
  shape: TableShape;
  rotated?: boolean;
  size: number;
  color: string;
  fill?: string;
}) {
  const isRect = shape === "rectangle";
  const w = isRect ? (rotated ? size * 0.4 : size * 0.85) : size * 0.62;
  const h = isRect ? (rotated ? size * 0.85 : size * 0.4) : size * 0.62;
  return (
    <div
      style={{
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: w,
          height: h,
          borderRadius: shape === "round" ? "50%" : shape === "square" ? "4px" : "var(--radius-sm)",
          border: `2px solid ${color}`,
          background: fill || "transparent",
        }}
      />
    </div>
  );
}

function tableFitsAt(
  tables: FloorMapTable[],
  x: number,
  y: number,
  w: number,
  h: number,
  excludeId?: string,
): boolean {
  for (let yy = y; yy < y + h; yy++) {
    for (let xx = x; xx < x + w; xx++) {
      const t = tableAt(tables, xx, yy);
      if (t && t.id !== excludeId) return false;
    }
  }
  return true;
}

export default function FloorMap() {
  const { user } = useCurrentUser();
  const canWrite = can(user, "branches", "write");
  const { t } = useTranslation();
  const { branchId } = useParams<{ branchId: string }>();
  const nav = useNavigate();

  const [area, setArea] = useState<FloorMapArea | null>(null);
  const [tool, setTool] = useState<TableShape>("square");
  const [toolRotated, setToolRotated] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ghost, setGhost] = useState<{ x: number; y: number; w: number; h: number; valid: boolean } | null>(null);
  const [drag, setDrag] = useState<{ table: FloorMapTable; offsetPxX: number; offsetPxY: number } | null>(null);
  const [dragPreview, setDragPreview] = useState<{ x: number; y: number; valid: boolean } | null>(null);
  const dragPreviewRef = useRef(dragPreview);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [branchName, setBranchName] = useState("");

  const canvasRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dragPreviewRef.current = dragPreview;
  }, [dragPreview]);

  useEffect(() => {
    const currentDrag = drag;
    const currentArea = area;
    if (!currentDrag || !currentArea) return;
    const { table, offsetPxX, offsetPxY } = currentDrag;
    const { width: areaWidth, height: areaHeight, tables: areaTables } = currentArea;
    function onMove(e: MouseEvent) {
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const rawX = e.clientX - rect.left - offsetPxX;
      const rawY = e.clientY - rect.top - offsetPxY;
      const x = Math.floor(rawX / CELL_PX);
      const y = Math.floor(rawY / CELL_PX);
      const w = table.width;
      const h = table.height;

      if (x < 0 || y < 0 || x + w > areaWidth || y + h > areaHeight) {
        setDragPreview({ x, y, valid: false });
        return;
      }
      const valid = tableFitsAt(areaTables, x, y, w, h, table.id);
      setDragPreview({ x, y, valid });
    }
    function onUp() {
      const preview = dragPreviewRef.current;
      if (preview?.valid && (preview.x !== table.x || preview.y !== table.y)) {
        updateTable(table.id, { x: preview.x, y: preview.y });
      }
      setDrag(null);
      setDragPreview(null);
      dragPreviewRef.current = null;
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [drag, area]);

  useEffect(() => {
    if (!branchId) return;
    setLoading(true);
    api
      .get<FloorMapArea>(`/branches/${branchId}/floor-map`)
      .then(({ data }) => {
        setArea(data);
      })
      .catch(() => setErr(t("floorMap.loadError")))
      .finally(() => setLoading(false));
  }, [branchId, t]);

  // Fetch branch name from the public list so we can show it in the header.
  useEffect(() => {
    if (!branchId) return;
    api
      .get<{ id: string; name: string }[]>("/branches?limit=500")
      .then(({ data }) => {
        const b = data.find((x) => x.id === branchId);
        if (b) setBranchName(b.name);
      })
      .catch(() => {});
  }, [branchId]);

  const selectedTable = useMemo(
    () => area?.tables.find((t) => t.id === selectedId) ?? null,
    [area, selectedId],
  );

  const selectedPos = useMemo(() => {
    if (!selectedTable || !area) return null;
    const MODAL_W = 200;
    const MODAL_H = 240;
    const canvasW = area.width * CELL_PX;
    const canvasH = area.height * CELL_PX;

    const right = (selectedTable.x + selectedTable.width) * CELL_PX + 8;
    const topRight = selectedTable.y * CELL_PX;
    if (right + MODAL_W <= canvasW && topRight + MODAL_H <= canvasH) {
      return { left: right, top: topRight };
    }

    const left = selectedTable.x * CELL_PX - MODAL_W - 8;
    const topLeft = selectedTable.y * CELL_PX;
    if (left >= 0 && topLeft + MODAL_H <= canvasH) {
      return { left, top: topLeft };
    }

    const bottom = (selectedTable.y + selectedTable.height) * CELL_PX + 8;
    const leftBottom = selectedTable.x * CELL_PX;
    if (leftBottom + MODAL_W <= canvasW && bottom + MODAL_H <= canvasH) {
      return { left: leftBottom, top: bottom };
    }

    const top = selectedTable.y * CELL_PX - MODAL_H - 8;
    const leftTop = selectedTable.x * CELL_PX;
    if (leftTop + MODAL_W <= canvasW && top >= 0) {
      return { left: leftTop, top };
    }

    return { left: 8, top: 8 };
  }, [selectedTable, area]);

  function updateArea(patch: Partial<FloorMapArea>) {
    setArea((prev) => (prev ? { ...prev, ...patch } : prev));
  }

  function updateTable(id: string, patch: Partial<FloorMapTable>) {
    setArea((prev) =>
      prev
        ? {
            ...prev,
            tables: prev.tables.map((t) => (t.id === id ? { ...t, ...patch } : t)),
          }
        : prev,
    );
  }

  function deleteTable(id: string) {
    setArea((prev) =>
      prev ? { ...prev, tables: prev.tables.filter((t) => t.id !== id) } : prev,
    );
    if (selectedId === id) setSelectedId(null);
  }

  function clearAll() {
    if (!area) return;
    if (window.confirm(t("floorMap.clearAllConfirm"))) {
      updateArea({ tables: [] });
      setSelectedId(null);
    }
  }

  function toggleTableRotation(id: string) {
    const t = area?.tables.find((x) => x.id === id);
    if (!t) return;
    if (t.shape === "round" || t.shape === "square") return;
    updateTable(id, {
      width: t.height,
      height: t.width,
      rotation: t.rotation === 90 ? 0 : 90,
    });
  }

  function handleCanvasMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!canWrite || !area || !canvasRef.current || drag) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / CELL_PX);
    const y = Math.floor((e.clientY - rect.top) / CELL_PX);
    const { w, h } = shapeSize(tool, toolRotated);

    if (w > area.width || h > area.height) {
      setGhost(null);
      return;
    }
    if (x < 0 || y < 0 || x >= area.width || y >= area.height) {
      setGhost(null);
      return;
    }
    if (tableAt(area.tables, x, y)) {
      setGhost(null);
      return;
    }

    const gx = Math.max(0, Math.min(x, area.width - w));
    const gy = Math.max(0, Math.min(y, area.height - h));
    const valid = tableFitsAt(area.tables, gx, gy, w, h);
    setGhost({ x: gx, y: gy, w, h, valid });
  }

  function handleCanvasMouseLeave() {
    setGhost(null);
  }

  function handleCanvasClick() {
    if (!area || drag) return;

    if (selectedId) {
      setSelectedId(null);
    }

    if (!canWrite) return;

    if (ghost?.valid) {
      const number = nextNumber(area.tables);
      const table = newTable(ghost.x, ghost.y, tool, toolRotated, number);
      updateArea({ tables: [...area.tables, table] });
      setSelectedId(table.id);
      setErr(null);
    }
  }

  function startTableDrag(e: React.MouseEvent<HTMLDivElement>, table: FloorMapTable) {
    e.stopPropagation();
    e.preventDefault();
    setSelectedId(table.id);
    if (!canWrite || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const offsetPxX = e.clientX - rect.left - table.x * CELL_PX;
    const offsetPxY = e.clientY - rect.top - table.y * CELL_PX;
    setDrag({ table, offsetPxX, offsetPxY });
    setDragPreview(null);
  }

  async function save() {
    if (!area || !branchId) return;
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        id: area.id && area.id !== EMPTY_UUID ? area.id : undefined,
        name: area.name,
        width: area.width,
        height: area.height,
        tables: area.tables,
      };
      const { data } = await api.put<FloorMapArea>(`/branches/${branchId}/floor-map`, payload);
      setArea(data);
      setSelectedId(null);
    } catch (e: any) {
      const msg = e?.response?.data?.detail;
      setErr(typeof msg === "string" ? msg : t("floorMap.saveError"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <IconLoader size={32} className="muted" />
      </div>
    );
  }

  if (!area) {
    return (
      <div className="page-header">
        <h1>{t("floorMap.title")}</h1>
        {err && <p className="text-red">{err}</p>}
      </div>
    );
  }

  return (
    <div>
      <div className="page-header" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <button className="btn-ghost" onClick={() => nav(-1)} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <IconChevronLeft size={18} />
          {t("common.back")}
        </button>
        <div>
          <h1>{t("floorMap.title")}</h1>
          <p style={{ color: "var(--muted)" }}>
            {branchName ? `${branchName} — ${t("branches.title")}` : t("branches.title")}
          </p>
        </div>
      </div>

      {err && (
        <div className="card" style={{
          padding: "12px 16px", marginBottom: 20,
          background: "var(--red-dim)", color: "var(--red)",
          display: "flex", alignItems: "center", gap: 8, fontSize: 14,
        }}>
          <IconAlert size={16} />
          {err}
        </div>
      )}

      <div className="card" style={{ display: "flex", gap: 24, padding: 24, flexWrap: "wrap" }}>
        {/* Toolbar */}
        {canWrite && (
        <div style={{ flex: "0 0 220px", minWidth: 220 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>{t("floorMap.placeTable")}</h3>

          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {(["round", "square", "rectangle"] as TableShape[]).map((s) => {
              const active = tool === s;
              return (
                <button
                  key={s}
                  className={active ? "primary" : "btn-ghost"}
                  onClick={() => setTool(s)}
                  title={t(`floorMap.shape.${s}`)}
                  aria-label={t(`floorMap.shape.${s}`)}
                  style={{ flex: 1, padding: 8, display: "flex", alignItems: "center", justifyContent: "center" }}
                >
                  <ShapeIcon
                    shape={s}
                    rotated={toolRotated}
                    size={28}
                    color={active ? "var(--bg)" : "var(--text)"}
                    fill={active ? "rgba(255,255,255,0.25)" : "transparent"}
                  />
                </button>
              );
            })}
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer", marginBottom: 20 }}>
            <input
              type="checkbox"
              checked={toolRotated}
              onChange={(e) => setToolRotated(e.target.checked)}
            />
            {t("floorMap.rotate")}
          </label>

          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>{t("floorMap.areaSettings")}</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
            <div>
              <label>{t("floorMap.areaName")}</label>
              <input
                value={area.name}
                onChange={(e) => updateArea({ name: e.target.value })}
              />
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label>{t("floorMap.width")}</label>
                <input
                  type="number"
                  min={1}
                  value={area.width}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (Number.isFinite(v) && v > 0) updateArea({ width: v });
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label>{t("floorMap.height")}</label>
                <input
                  type="number"
                  min={1}
                  value={area.height}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (Number.isFinite(v) && v > 0) updateArea({ height: v });
                  }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button className="primary" onClick={save} disabled={saving}>
              {saving ? <IconLoader size={18} /> : t("common.save")}
            </button>
            <button className="btn-ghost" onClick={clearAll}>
              {t("floorMap.clearAll")}
            </button>
          </div>
        </div>
        )}

        {/* Canvas */}
        <div style={{ flex: 1, minWidth: 300, overflow: "auto" }}>
          <div
            ref={canvasRef}
            onClick={handleCanvasClick}
            onMouseMove={handleCanvasMouseMove}
            onMouseLeave={handleCanvasMouseLeave}
            style={{
              position: "relative",
              width: area.width * CELL_PX,
              height: area.height * CELL_PX,
              backgroundColor: "var(--bg-elevated)",
              backgroundImage: `
                linear-gradient(to right, var(--border) 1px, transparent 1px),
                linear-gradient(to bottom, var(--border) 1px, transparent 1px)
              `,
              backgroundSize: `${CELL_PX}px ${CELL_PX}px`,
              border: "1px solid var(--border)",
              cursor: "crosshair",
            }}
          >
            {area.tables.map((t) => {
              const selected = selectedId === t.id;
              const isDragged = drag?.table.id === t.id;
              return (
                <div
                  key={t.id}
                  onMouseDown={(e) => startTableDrag(e, t)}
                  style={{
                    position: "absolute",
                    left: t.x * CELL_PX + 1,
                    top: t.y * CELL_PX + 1,
                    width: t.width * CELL_PX - 2,
                    height: t.height * CELL_PX - 2,
                    background: selected ? "var(--accent)" : "var(--panel)",
                    color: selected ? "var(--bg)" : "var(--text)",
                    border: `2px solid ${selected ? "var(--accent-hover)" : "var(--text)"}`,
                    borderRadius: t.shape === "round" ? "50%" : "var(--radius-sm)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: !canWrite ? "pointer" : drag ? (isDragged ? "grabbing" : "default") : "move",
                    userSelect: "none",
                    opacity: isDragged ? 0.35 : 1,
                    pointerEvents: isDragged ? "none" : "auto",
                    zIndex: selected ? 5 : 1,
                  }}
                >
                  {t.number}
                </div>
              );
            })}

            {ghost && (
              <div
                style={{
                  position: "absolute",
                  left: ghost.x * CELL_PX + 1,
                  top: ghost.y * CELL_PX + 1,
                  width: ghost.w * CELL_PX - 2,
                  height: ghost.h * CELL_PX - 2,
                  background: ghost.valid ? "var(--accent)" : "var(--red)",
                  opacity: ghost.valid ? 0.25 : 0.2,
                  border: `2px dashed ${ghost.valid ? "var(--accent)" : "var(--red)"}`,
                  borderRadius: tool === "round" ? "50%" : "var(--radius-sm)",
                  pointerEvents: "none",
                  zIndex: 2,
                }}
              />
            )}

            {drag && dragPreview && (
              <div
                style={{
                  position: "absolute",
                  left: dragPreview.x * CELL_PX + 1,
                  top: dragPreview.y * CELL_PX + 1,
                  width: drag.table.width * CELL_PX - 2,
                  height: drag.table.height * CELL_PX - 2,
                  background: dragPreview.valid ? "var(--panel)" : "var(--red)",
                  color: dragPreview.valid ? "var(--text)" : "var(--bg)",
                  border: `2px dashed ${dragPreview.valid ? "var(--text)" : "var(--red)"}`,
                  borderRadius: drag.table.shape === "round" ? "50%" : "var(--radius-sm)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  opacity: 0.85,
                  pointerEvents: "none",
                  userSelect: "none",
                  zIndex: 10,
                }}
              >
                {drag.table.number}
              </div>
            )}

            {selectedTable && selectedPos && !drag && (
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseMove={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  left: selectedPos.left,
                  top: selectedPos.top,
                  width: 200,
                  background: "var(--panel)",
                  border: "1px solid var(--border-light)",
                  borderRadius: "var(--radius)",
                  padding: 12,
                  boxShadow: "0 8px 30px rgba(0,0,0,0.45)",
                  zIndex: 20,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontWeight: 600 }}>{t("floorMap.tableSettings")}</span>
                  <button
                    className="btn-ghost"
                    onClick={() => setSelectedId(null)}
                    style={{ padding: 4 }}
                    title={t("common.cancel")}
                    aria-label={t("common.cancel")}
                  >
                    <IconX size={16} />
                  </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div>
                    <label>{t("floorMap.number")}</label>
                    <input
                      value={selectedTable.number}
                      disabled={!canWrite}
                      onChange={(e) => updateTable(selectedTable.id, { number: e.target.value })}
                    />
                  </div>
                  <div>
                    <label>{t("floorMap.seats")}</label>
                    <input
                      type="number"
                      min={0}
                      value={selectedTable.seats}
                      disabled={!canWrite}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (Number.isFinite(v) && v >= 0) updateTable(selectedTable.id, { seats: v });
                      }}
                    />
                  </div>
                  {selectedTable.shape === "rectangle" && (
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={selectedTable.rotation === 90}
                        disabled={!canWrite}
                        onChange={() => toggleTableRotation(selectedTable.id)}
                      />
                      {t("floorMap.rotate")}
                    </label>
                  )}
                  {canWrite && (
                    <button
                      className="btn-ghost"
                      style={{ color: "var(--red)" }}
                      onClick={() => deleteTable(selectedTable.id)}
                    >
                      {t("floorMap.deleteTable")}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
